/**
 * Bascule de compte sur le même téléphone (migration 0301).
 *
 * - `SwitchToChildDialog` (parent) : si le parent n'a pas encore de code PIN,
 *   il le choisit (deux fois) ; puis confirmation et passage sur le compte de
 *   l'enfant.
 * - `SwitchBackDialog` (enfant) : code PIN du parent pour revenir sur son
 *   compte. « Code oublié » : déconnexion, le parent se reconnecte par email.
 *
 * Modales rendues dans un portail (jamais enfermées dans un bloc animé).
 */

import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import {
  PIN_PATTERN,
  switchBackToParent,
  switchToChild,
  useHasSwitchPin,
  useSetSwitchPin,
} from '@/lib/parent/managed-children';
import { PinInput } from './pin-input';

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="ocean:bg-[#061433] ocean:border ocean:border-white/10 w-full max-w-sm rounded-3xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-ink-900 text-lg font-bold leading-snug">{title}</h2>
          <button
            type="button"
            aria-label="Fermer"
            onClick={onClose}
            className="text-ink-500 -mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="mt-3 flex flex-col gap-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export function SwitchToChildDialog({
  childId,
  childName,
  onClose,
}: {
  childId: string;
  childName: string;
  onClose: () => void;
}) {
  const hasPin = useHasSwitchPin(true);
  const setPin = useSetSwitchPin();
  const [pin, setPinValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function go() {
    setError(null);
    setBusy(true);
    try {
      await switchToChild(childId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La bascule a échoué.');
      setBusy(false);
    }
  }

  if (hasPin.isLoading) {
    return (
      <Modal title={`Passer sur le compte de ${childName}`} onClose={onClose}>
        <div className="bg-ink-100 h-24 animate-pulse rounded-xl" />
      </Modal>
    );
  }

  if (!hasPin.data) {
    const mismatch = confirm.length === 4 && pin !== confirm;
    return (
      <Modal title="Choisis ton code parent" onClose={onClose}>
        <p className="text-ink-600 text-sm">
          Ce code à 4 chiffres te sera demandé pour revenir sur ton compte quand {childName} aura
          fini. Ne le donne pas à ton enfant.
        </p>
        <PinInput id="pin-new" label="Code parent" value={pin} onChange={setPinValue} autoFocus />
        <PinInput id="pin-confirm" label="Confirme le code" value={confirm} onChange={setConfirm} />
        {(mismatch || setPin.error || error) && (
          <p className="text-sm text-red-600" role="alert">
            {mismatch ? 'Les deux codes sont différents.' : (setPin.error?.message ?? error)}
          </p>
        )}
        <Button
          fullWidth
          disabled={!PIN_PATTERN.test(pin) || pin !== confirm || setPin.isPending || busy}
          onClick={() => setPin.mutate(pin, { onSuccess: () => void go() })}
        >
          {setPin.isPending || busy ? 'Un instant…' : `Enregistrer et passer sur ${childName}`}
        </Button>
      </Modal>
    );
  }

  return (
    <Modal title={`Passer sur le compte de ${childName} ?`} onClose={onClose}>
      <p className="text-ink-600 text-sm">
        Ton téléphone affichera le compte de {childName}. Pour revenir sur le tien, il faudra ton
        code parent.
      </p>
      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      <Button fullWidth disabled={busy} onClick={() => void go()}>
        {busy ? 'Un instant…' : `Passer sur ${childName}`}
      </Button>
    </Modal>
  );
}

export function SwitchBackDialog({ onClose }: { onClose: () => void }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      await switchBackToParent(pin);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Code incorrect.');
      setPin('');
      setBusy(false);
    }
  }

  return (
    <Modal title="Revenir au compte parent" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (PIN_PATTERN.test(pin) && !busy) void submit();
        }}
      >
        <PinInput id="pin-back" label="Code parent" value={pin} onChange={setPin} autoFocus />
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" fullWidth disabled={!PIN_PATTERN.test(pin) || busy}>
          {busy ? 'Vérification…' : 'Revenir à mon compte'}
        </Button>
      </form>
      <button
        type="button"
        className="text-primary-600 min-h-[2.75rem] text-sm font-semibold"
        onClick={() => {
          void supabase.auth
            .signOut({ scope: 'local' })
            .then(() => window.location.assign('/login'));
        }}
      >
        Code oublié ? Me reconnecter par email
      </button>
    </Modal>
  );
}
